import React, { useMemo, useState } from "react";
import { Box, Button, CircularProgress as MuiCircularProgress, LinearProgress, TextField } from "@mui/material";
import { CircularProgress, Grid, Typography } from "@mui/joy";
import { useMutation, useQuery } from "@apollo/client";
import { useTranslation } from "react-i18next";
import { toast } from "react-toastify";
import _ from "lodash";
import { BasicModalContent } from "@dreebit-registry/vsm-microservice-components";
import { InstalledBaseNodeEventContext } from "../../graphql/queries/installedBaseNodeEventContext";
import { InstalledBaseEventListTDAs } from "../../graphql/queries/installedBaseEventListTDAs";
import { CreateInstalledBaseEvent } from "../../graphql/mutations/createInstalledBaseEvent";
import { InstalledBaseNodeTypeKey, ManualEventType } from "../../codegen/graphql";
import { useEventTypeLabels } from "../../utils/eventTypeLabels";
import { getEventTypeIcon } from "../../utils/eventTypeIcons";
import { resolveErrorMessage } from "../../utils/resolveErrorMessage";
import { CommissioningAttribute, deriveCommissioningSteps } from "../../utils/deriveCommissioningSteps";
import AssetNameplate from "../AssetNameplate";
import CommissioningChecklist from "./CommissioningChecklist";

export interface IotCommissioningWizardProps {
    assetId: string;
    assetLocationId: string;
    /** The asset's own (ASSET-typed) installed base node. */
    installedBaseNodeId: string;
    onClose: () => void;
    /** Called after the event was created (before onClose) — refetch what carries the SLA status. */
    onSuccess?: () => void;
}

/** Queries that carry the SLA status, the event count / history or the commissioning event itself. */
const REFETCH_QUERIES = ["InstalledBaseEventList", "AssetDashboardHero", "LatestIotCommissioningEvent", "AssetIoTSlaStatus"];

/**
 * IoT commissioning checklist (IBM-1045) as a single-page checklist. The
 * checks come from the backend's CREATE form of `ASSET_IOT_COMMISSIONING`
 * (question = `name`, acceptance criterion = `hint`, order = backend order),
 * followed by the optional message; submitting requires every `must_accept`
 * check to be confirmed;
 * the result is an ordinary installed base event created through
 * `createInstalledBaseEvent`. The event type is never offered by
 * `availableInstalledBaseEvents`, hence this dedicated wizard instead of the
 * generic event modal (whose shared TDAForm neither shows hints on
 * checkboxes nor enforces `must_accept`).
 */
const IotCommissioningWizard = ({ assetId, assetLocationId, installedBaseNodeId, onClose, onSuccess }: IotCommissioningWizardProps) => {
    const { t } = useTranslation();
    const eventTypeLabels = useEventTypeLabels();

    // Parent node for `targetNodeId` — an ASSET-typed node walks up to its
    // parent, exactly like CreateInstalledBaseEventModal does for every event.
    const contextQuery = useQuery(InstalledBaseNodeEventContext, { variables: { id: installedBaseNodeId } });
    const nodeTypeKey = _.get(contextQuery.data, "installedBaseNode.nodeType.key") as string | undefined;
    const parentNodeId = _.get(contextQuery.data, "installedBaseNode.parentNode.id") as string | undefined;
    const targetNodeId = nodeTypeKey === InstalledBaseNodeTypeKey.ASSET && parentNodeId ? parentNodeId : installedBaseNodeId;

    // The form is loaded scoped to the asset's node, as InstalledBaseEventForm does.
    const tdaQuery = useQuery(InstalledBaseEventListTDAs, {
        variables: {
            id: installedBaseNodeId,
            typeDefinitionIndex: "INSTALLED_BASE_EVENT_CREATE",
            options: {
                eventType: ManualEventType.ASSET_IOT_COMMISSIONING,
                parentInstalledBaseNodeId: installedBaseNodeId,
                assetId,
            },
        },
        fetchPolicy: "network-only",
    });
    const attributes = _.get(tdaQuery.data, "installedBaseNode.installedBaseEventList.typeDefinitionAttributeList.typeDefinitionAttributes", []) as CommissioningAttribute[];
    const steps = useMemo(() => deriveCommissioningSteps(attributes), [attributes]);
    const checkSteps = useMemo(() => steps.filter((step) => step.kind === "check"), [steps]);
    const messageAttribute = steps.find((step) => step.kind === "message")?.attribute;

    const [checked, setChecked] = useState<Record<string, boolean>>({});
    const [message, setMessage] = useState("");

    const [createEvent, { loading: submitting }] = useMutation(CreateInstalledBaseEvent, { refetchQueries: REFETCH_QUERIES });

    const confirmedCount = checkSteps.filter((check) => checked[check.attribute.index]).length;
    const canSubmit = checkSteps.length > 0
        && checkSteps.every((check) => check.kind !== "check" || !check.mustAccept || checked[check.attribute.index]);

    const handleFinish = async () => {
        if (submitting || !canSubmit) return;
        try {
            const attributesInput = [
                ...checkSteps.map((check) => ({ attributeIndex: check.attribute.index, value: true })),
                ...(messageAttribute ? [{ attributeIndex: messageAttribute.index, value: message.trim() ? message.trim() : null }] : []),
            ];
            const result = await createEvent({
                variables: {
                    event: { assetId, eventType: ManualEventType.ASSET_IOT_COMMISSIONING, targetNodeId },
                    attributes: attributesInput,
                },
            });
            if (!_.get(result, "data.createInstalledBaseEvent.id")) {
                toast.error(resolveErrorMessage({ graphQLErrors: result.errors }, t));
                return;
            }
            toast.success(t("Commissioning completed. The 24h connectivity verification starts automatically."));
            onSuccess?.();
            onClose();
        } catch (err) {
            toast.error(resolveErrorMessage(err, t));
        }
    };

    const renderBody = () => {
        if (tdaQuery.loading || contextQuery.loading) {
            return (
                <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
                    <CircularProgress />
                </Box>
            );
        }
        if (tdaQuery.error || steps.length === 0) {
            return (
                <Typography level="body-md" sx={{ color: "error.main", py: 2 }}>
                    {t("Error loading form.")}
                </Typography>
            );
        }
        const total = checkSteps.length;
        return (
            <>
                <Box sx={{ py: 2, display: "flex", flexDirection: "column", gap: "16px" }}>
                    <CommissioningChecklist
                        checks={checkSteps.map((check) => check.attribute)}
                        checked={checked}
                        onToggle={(index, value) => setChecked((prev) => ({ ...prev, [index]: value }))}
                        disabled={submitting}
                    />
                    {messageAttribute && (
                        <TextField
                            multiline
                            minRows={3}
                            fullWidth
                            label={messageAttribute.name}
                            helperText={messageAttribute.hint ?? undefined}
                            value={message}
                            onChange={(event) => setMessage(event.target.value)}
                            disabled={submitting}
                            inputProps={{ "data-testid": "commissioning-message" }}
                        />
                    )}
                </Box>
                {/* Sticky footer: the dialog paper scrolls, progress and actions stay in view.
                    The negative margins cancel BasicModalContent's 16px padding so the white
                    band spans the full width and ends flush with the paper. */}
                <Box
                    sx={{
                        position: "sticky",
                        bottom: 0,
                        zIndex: 1,
                        mx: "-16px",
                        mb: "-16px",
                        px: "16px",
                        pt: "12px",
                        pb: "16px",
                        backgroundColor: "#fff",
                        borderTop: "1px solid #eee",
                        display: "flex",
                        flexDirection: "column",
                        gap: "12px",
                    }}
                >
                    <Box data-testid="commissioning-progress">
                        <Typography level="body-sm" sx={{ color: "text.secondary", mb: "4px" }}>
                            {t("{{count}} of {{total}} checks confirmed", { count: confirmedCount, total })}
                        </Typography>
                        <LinearProgress
                            variant="determinate"
                            value={total ? (confirmedCount / total) * 100 : 0}
                            sx={{ height: 6, borderRadius: 3, backgroundColor: "#eee", "& .MuiLinearProgress-bar": { backgroundColor: confirmedCount === total ? "#599460" : undefined } }}
                        />
                    </Box>
                    <Grid container spacing={1}>
                        <Grid xs={6}>
                            <Button fullWidth variant="outlined" onClick={onClose} disabled={submitting} data-testid="commissioning-cancel">
                                {t("Cancel")}
                            </Button>
                        </Grid>
                        <Grid xs={6}>
                            <Button
                                fullWidth
                                variant="contained"
                                onClick={() => { void handleFinish(); }}
                                disabled={!canSubmit || submitting}
                                data-testid="commissioning-submit"
                                startIcon={submitting ? <MuiCircularProgress size={16} /> : undefined}
                            >
                                {t("Complete commissioning")}
                            </Button>
                        </Grid>
                    </Grid>
                </Box>
            </>
        );
    };

    return (
        <BasicModalContent
            startIcon={getEventTypeIcon(ManualEventType.ASSET_IOT_COMMISSIONING, {})}
            onClose={submitting ? () => undefined : onClose}
            title={<Typography level="titleSmallLight">{eventTypeLabels[ManualEventType.ASSET_IOT_COMMISSIONING]}</Typography>}
        >
            <Box sx={{ position: "relative" }} data-testid="commissioning-wizard">
                {/* Same compact nameplate as the event modal, so the user sees which asset is commissioned. */}
                <Box sx={{ mt: 1, mb: 1, p: 2, border: "1px solid #ccc", borderRadius: "5px" }}>
                    <AssetNameplate assetLocationId={assetLocationId} compact disableNavigation />
                </Box>
                <Typography level="body-sm" sx={{ color: "text.secondary" }}>
                    {t("Confirm every check to start the 24h connectivity verification.")}
                </Typography>
                {renderBody()}
            </Box>
        </BasicModalContent>
    );
};

export default IotCommissioningWizard;
